import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { ConditionBuilder } from '../condition-builder/ConditionBuilder';
import { useKeyboardSave } from './use-keyboard-save';
import type { FunctionElement, Condition } from '@/types/policy-editor';

interface FunctionFormProps {
    element: FunctionElement | null;
    onSave: (element: FunctionElement) => void;
    onCancel: () => void;
}

export function FunctionForm({ element, onSave, onCancel }: FunctionFormProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [parameters, setParameters] = useState<string[]>([]);
    const [returnValue, setReturnValue] = useState('');
    const [conditions, setConditions] = useState<Condition[]>([]);

    useEffect(() => {
        if (element) {
            setName(element.name);
            setDescription(element.description ?? '');
            setParameters([...element.parameters]);
            setReturnValue(element.returnValue);
            setConditions(element.conditions);
        } else {
            setName(''); setDescription(''); setParameters([]); setReturnValue(''); setConditions([]);
        }
    }, [element]);

    const handleSave = useCallback(() => {
        if (!name.trim()) return;
        onSave({
            id: element?.id ?? `el-${Date.now()}`,
            elementType: 'function',
            name,
            description: description || undefined,
            parameters: parameters.filter(Boolean),
            returnValue,
            conditions,
        });
    }, [element, name, description, parameters, returnValue, conditions, onSave]);

    useKeyboardSave(handleSave, !!name.trim());

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div className="rounded-md bg-purple-50 px-3 py-2 text-xs text-purple-700">
                    Functions are reusable logic blocks that can be called from rules and other
                    functions. Define multiple functions with the same name for OR logic.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="fn-name">Function name</Label>
                        <Input id="fn-name" placeholder="e.g. cap_value, get_tier" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="fn-desc">Description</Label>
                        <Input id="fn-desc" placeholder="What this function does" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label>Parameters</Label>
                    <div className="space-y-1.5">
                        {parameters.map((param, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <Input
                                    className="h-8 font-mono text-xs"
                                    placeholder={`param${i + 1}`}
                                    value={param}
                                    onChange={(e) => {
                                        const next = [...parameters];
                                        next[i] = e.target.value;
                                        setParameters(next);
                                    }}
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0 text-slate-400 hover:text-red-500"
                                    onClick={() => setParameters(parameters.filter((_, j) => j !== i))}
                                >
                                    <X size={14} />
                                </Button>
                            </div>
                        ))}
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setParameters([...parameters, ''])}>
                            <Plus size={12} className="mr-1" />
                            Add parameter
                        </Button>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="fn-return">Return value</Label>
                    <Input id="fn-return" className="font-mono text-xs" placeholder="e.g. value, true, result" value={returnValue} onChange={(e) => setReturnValue(e.target.value)} />
                    <p className="text-[11px] text-slate-400">The expression this function evaluates to when conditions are met</p>
                </div>

                <ConditionBuilder conditions={conditions} onChange={setConditions} />
            </div>

            <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-[10px] text-slate-400">Ctrl+Enter to save</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
                    <Button size="sm" onClick={handleSave} disabled={!name.trim()}>Save</Button>
                </div>
            </div>
        </div>
    );
}
